import React, { useState, useMemo } from "react";
import { css } from "@emotion/css";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaUserCircle, FaLock } from "react-icons/fa";

import { newLogo } from "../../assets/images";
import { setUserKey } from "../../utils/auth";
import { config } from "../../config";
import Loader from "../../common/loader/Loader";
import { useUser } from "../../context/UserContext";
// Emotion Styles
const containerStyle = css`
    min-height: 100vh;
    width: 100%;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    @media (min-width: 768px) {
        flex-direction: row;
        min-height: 100vh;
    }
`;

const leftPaneStyle = css`
    background-color: #fcac15;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 24px;
    color: white;
    text-align: center;
    @media (min-width: 768px) {
        flex: 2;
        padding: 24px;
    }
`;

const rightPaneStyle = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 24px;
    @media (min-width: 768px) {
        flex: 3;
        padding: 24px;
    }
`;

const formStyle = css`
    width: 100%;
    max-width: 400px;
    margin-top: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const inputGroupStyle = css`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const inputLabelStyle = css`
    font-size: 14px;
    color: #333;
    font-weight: 500;
`;

const inputWrapperStyle = css`
    display: flex;
    align-items: center;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 8px 12px;
    &:focus-within {
        border-color: #fcac15;
    }
`;

const inputStyle = css`
    flex: 1;
    border: none;
    outline: none;
    padding: 4px;
    font-size: 16px;
    color: #333;
`;

const iconStyle = css`
    color: #777;
    margin-right: 8px;
`;

const errorTextStyle = css`
    color: #d32f2f;
    font-size: 12px;
    margin-top: 4px;
`;

const buttonStyle = css`
    background-color: #fc3c3c;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    transition: background-color 0.2s;
    &:hover {
        background-color: #b91c1c;
    }
    &:disabled {
        background-color: #ccc;
        cursor: not-allowed;
    }
`;

const signupButtonStyle = css`
    background-color: #2563eb;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 10px 24px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;
    margin-top: 16px;
    width: auto;
    &:hover {
        background-color: #1d4ed8;
    }
`;

const helperLinkStyle = css`
    background: none;
    border: none;
    color: #333;
    font-size: 14px;
    cursor: pointer;
    text-decoration: underline;
    &:hover {
        color: #000;
    }
`;

const textCenterStyle = css`
    text-align: center;
    color: #333;
    font-size: 14px;
    margin-top: 16px;
`;

const checkboxWrapperStyle = css`
    display: flex;
    align-items: center;
    gap: 8px;
    color: #333;
    font-size: 14px;
`;

const loginTypeRowStyle = css`
    display: flex;
    gap: 12px;
    margin-bottom: 8px;
`;

const loginTypeButtonStyle = (active) => css`
    flex: 1;
    padding: 10px 12px;
    border: 2px solid ${active ? "#fcac15" : "#ddd"};
    border-radius: 6px;
    background-color: ${active ? "#fff8e6" : "#fff"};
    color: ${active ? "#333" : "#666"};
    font-size: 13px;
    font-weight: ${active ? "600" : "500"};
    cursor: pointer;
    transition: border-color 0.2s, background-color 0.2s;
    &:hover {
        border-color: #fcac15;
        background-color: #fff8e6;
    }
`;

const LOGIN_TYPE = {
    EMAIL_OR_MOBILE: "email_or_mobile",
    MEMBER_ID: "member_id",
};

function getValidationSchema(loginType) {
    const isEmailOrMobile = loginType === LOGIN_TYPE.EMAIL_OR_MOBILE;
    return yup.object({
        username: yup.string().required(isEmailOrMobile ? "Email ID is Required" : "Member ID is Required"),
        password: yup.string().required(isEmailOrMobile ? "Mobile Number is Required" : "Password is Required"),
    });
}

export default function Login() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [loginType, setLoginType] = useState(LOGIN_TYPE.EMAIL_OR_MOBILE);
    const { fetchProfile } = useUser();

    const validationSchema = useMemo(() => getValidationSchema(loginType), [loginType]);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            username: localStorage.getItem("rememberedUsername") || "",
            rememberMe: localStorage.getItem("rememberedUsername") ? true : false,
        }
    });

    const onSubmit = async (values) => {
        setIsLoading(true);
        try {
            const payload = {
                username: values.username,
                password: values.password,
            };

            const response = await fetch(`${config.serverUrl}/api/auth/login`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            if (response.ok && result.status === "success") {
                if (values.rememberMe) {
                    localStorage.setItem("rememberedUsername", values.username);
                } else {
                    localStorage.removeItem("rememberedUsername");
                }

                // We need to shape the data so auth.js and other components that check `user.id` or `user.token` still work seamlessly.
                // The new API returns `{ token, refreshToken, user: { id, memberId, ... } }`.
                const sessionData = {
                    token: result.data.token,
                    refreshToken: result.data.refreshToken,
                    ...result.data.user
                };

                setUserKey(sessionData);
                fetchProfile();
                navigate("/dashboard");
                toast.success(result.message || "Login successful!");
            } else {
                toast.error(result.message || "Failed to login!");
            }
        } catch (error) {
            toast.error("Something went wrong!");
            console.log("Login error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const hasError = (element) => !!errors[element];
    const getError = (element) => {
        if (errors[element]) {
            return Object.values(errors[element])[0];
        }
    };

    const isEmailOrMobile = loginType === LOGIN_TYPE.EMAIL_OR_MOBILE;
    const firstFieldLabel = isEmailOrMobile ? "Email ID" : "Member ID";
    const secondFieldLabel = isEmailOrMobile ? "Mobile Number" : "Password";
    const firstFieldPlaceholder = isEmailOrMobile
        ? "Enter your email ID"
        : "Enter your member ID";
    const secondFieldPlaceholder = isEmailOrMobile
        ? "Enter your mobile number"
        : "Enter your password";

    const onSignup = () => {
        navigate("/registration");
    };

    return (
        <div className={containerStyle}>
            {/* Left Pane */}
            <div className={leftPaneStyle}>
                <img
                    src={newLogo}
                    alt="Club Mango Friends Logo"
                    style={{ width: "120px", height: "120px", borderRadius: "50%", objectFit: "cover", marginBottom: "24px" }}
                />
                <h2 style={{ fontSize: "24px", margin: 0, fontWeight: "normal" }}>
                    Welcome to
                </h2>
                <h1 style={{ fontSize: "36px", margin: "8px 0", fontWeight: "bold" }}>
                    Club Mango Friends
                </h1>
            </div>

            {/* Right Pane */}
            <div className={rightPaneStyle}>
                <h2 style={{ color: "#333", marginBottom: "8px" }}>Sign In</h2>

                <form className={formStyle} onSubmit={handleSubmit(onSubmit)} noValidate>
                    <div className={inputGroupStyle}>
                        <span className={inputLabelStyle} style={{ marginBottom: 4, display: "block" }}>
                            Sign in with
                        </span>
                        <div className={loginTypeRowStyle}>
                            <button
                                type="button"
                                className={loginTypeButtonStyle(isEmailOrMobile)}
                                onClick={() => setLoginType(LOGIN_TYPE.EMAIL_OR_MOBILE)}
                            >
                                Email / Mobile
                            </button>
                            <button
                                type="button"
                                className={loginTypeButtonStyle(!isEmailOrMobile)}
                                onClick={() => setLoginType(LOGIN_TYPE.MEMBER_ID)}
                            >
                                Member ID
                            </button>
                        </div>
                    </div>

                    <div className={inputGroupStyle}>
                        <label htmlFor="username" className={inputLabelStyle}>
                            {firstFieldLabel}
                        </label>
                        <div className={inputWrapperStyle} style={{ borderColor: hasError("username") ? "#d32f2f" : "#ccc" }}>
                            <FaUserCircle className={iconStyle} />
                            <input
                                id="username"
                                type={isEmailOrMobile ? "email" : "text"}
                                className={inputStyle}
                                autoComplete={isEmailOrMobile ? "email" : "username"}
                                placeholder={firstFieldPlaceholder}
                                {...register("username")}
                            />
                        </div>
                        {hasError("username") && <span className={errorTextStyle}>{getError("username")}</span>}
                    </div>

                    <div className={inputGroupStyle}>
                        <label htmlFor="password" className={inputLabelStyle}>
                            {secondFieldLabel}
                        </label>
                        <div className={inputWrapperStyle} style={{ borderColor: hasError("password") ? "#d32f2f" : "#ccc" }}>
                            <FaLock className={iconStyle} />
                            <input
                                id="password"
                                type={isEmailOrMobile ? "tel" : "password"}
                                className={inputStyle}
                                autoComplete={isEmailOrMobile ? "tel" : "current-password"}
                                placeholder={secondFieldPlaceholder}
                                {...register("password")}
                            />
                        </div>
                        {hasError("password") && <span className={errorTextStyle}>{getError("password")}</span>}
                    </div>

                    <button type="submit" className={buttonStyle} disabled={isLoading}>
                        Login
                        {isLoading && <Loader show={true} />}
                    </button>

                    <div className={checkboxWrapperStyle}>
                        <input type="checkbox" id="rememberMe" {...register("rememberMe")} />
                        <label htmlFor="rememberMe">Remember Me</label>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "16px" }}>
                        <p className={textCenterStyle} style={{ marginTop: "8px", margin: 0 }}>
                            Helpline No: +919604489537
                        </p>
                    </div>

                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <button type="button" className={signupButtonStyle} onClick={onSignup}>
                            New User Signup
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
